import React, { useEffect, useRef } from 'react';
import { Mic, MicOff, MonitorUp, User } from 'lucide-react';
import { Badge } from '@/components/ui';
import { clsx } from 'clsx';

interface VideoTileProps {
  participantName: string;
  isScreenShare?: boolean;
  videoTrack?: MediaStreamTrack;
  track?: MediaStreamTrack;
  isSelf?: boolean;
  isAudioEnabled?: boolean;
  className?: string;
}

export const VideoTile: React.FC<VideoTileProps> = ({
  participantName,
  videoTrack,
  track,
  isScreenShare = false,
  isSelf = false,
  isAudioEnabled = true,
  className,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const activeTrack = videoTrack || track;

  useEffect(() => {
    const videoElement = videoRef.current;
    
    if (!videoElement || !activeTrack) {
      console.log('VideoTile: No video ref or track', { 
        hasRef: !!videoElement, 
        hasTrack: !!activeTrack,
        participantName 
      });
      return;
    }

    console.log('VideoTile: Setting up track for', participantName, {
      trackId: activeTrack.id,
      trackKind: activeTrack.kind,
      trackEnabled: activeTrack.enabled,
      trackReadyState: activeTrack.readyState,
      isScreenShare,
    });

    // Check if track is in valid state
    if (activeTrack.readyState !== 'live') {
      console.warn('VideoTile: Track not in live state:', activeTrack.readyState);
      
      // Try again after a short delay
      const retryTimeout = setTimeout(() => {
        if (activeTrack.readyState === 'live' && videoElement) {
          const stream = new MediaStream([activeTrack]);
          videoElement.srcObject = stream;
          videoElement.play().catch(console.error);
        }
      }, 500);
      
      return () => clearTimeout(retryTimeout);
    }

    try {
      const stream = new MediaStream([activeTrack]);
      videoElement.srcObject = stream;
      
      // Play with error handling
      const playPromise = videoElement.play();
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            console.log('VideoTile: Playing', participantName);
          })
          .catch((error) => {
            console.error('VideoTile: Play error:', error.message);
            
            // Retry on user interaction if autoplay blocked
            if (error.name === 'NotAllowedError') {
              const retryPlay = () => {
                videoElement.play()
                  .then(() => console.log('VideoTile: Playing after interaction'))
                  .catch(console.error);
                document.removeEventListener('click', retryPlay);
              };
              document.addEventListener('click', retryPlay, { once: true });
            }
          });
      }

      console.log('VideoTile: Track attached successfully');
    } catch (error) {
      console.error('VideoTile: Error setting up track:', error);
    }

    // Cleanup - IMPORTANT: Don't stop the track, just disconnect it
    return () => {
      if (videoElement && videoElement.srcObject) {
        console.log('VideoTile: Cleaning up video element for', participantName);
        
        // Get the stream
        const stream = videoElement.srcObject as MediaStream;
        
        // The track is managed by RealtimeKit, not by this component
        videoElement.srcObject = null;
        
        // Optional: You can pause the video element
        videoElement.pause();
        
        console.log('VideoTile: Disconnected stream (track still live)');
      }
    };
  }, [activeTrack, participantName, isScreenShare]);

  // Monitor track state changes
  useEffect(() => {
    if (!activeTrack) return;

    const handleTrackEnded = () => {
      console.log('VideoTile: Track ended for', participantName);
    };

    const handleTrackMute = () => {
      console.log('VideoTile: Track muted for', participantName);
    };

    const handleTrackUnmute = () => {
      console.log('VideoTile: Track unmuted for', participantName);
    };

    activeTrack.addEventListener('ended', handleTrackEnded);
    activeTrack.addEventListener('mute', handleTrackMute);
    activeTrack.addEventListener('unmute', handleTrackUnmute);

    return () => {
      activeTrack.removeEventListener('ended', handleTrackEnded);
      activeTrack.removeEventListener('mute', handleTrackMute);
      activeTrack.removeEventListener('unmute', handleTrackUnmute);
    };
  }, [activeTrack, participantName]);

  return (
    <div className={clsx('relative bg-gray-900 rounded-lg overflow-hidden', className)}>
      {/* Video Element */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={isSelf} // Only mute self to prevent echo
        className={clsx(
          'w-full h-full',
          isScreenShare ? 'object-contain' : 'object-cover',
          !activeTrack && 'hidden'
        )}
      />

      {/* Placeholder when no video track */}
      {!activeTrack && (
        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary-600 to-secondary-600">
          {isScreenShare ? (
            <MonitorUp size={64} className="text-white opacity-50" />
          ) : (
            <User size={64} className="text-white opacity-50" />
          )}
        </div>
      )}

      {/* Overlay Info */}
      <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/70 to-transparent">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {isScreenShare && (
              <Badge variant="primary" size="sm">
                <MonitorUp size={12} className="mr-1" />
                Screen
              </Badge>
            )}
            <span className="text-white text-sm font-medium truncate">
              {participantName} {isSelf && '(You)'}
            </span>
          </div>

          {/* Audio Status */}
          {!isScreenShare && (
            <div className="flex items-center gap-1">
              {isAudioEnabled ? (
                <Mic size={16} className="text-white" />
              ) : (
                <div className="bg-red-500 rounded-full p-1">
                  <MicOff size={14} className="text-white" />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Track state indicator */}
      {activeTrack && (
        <div className="absolute top-2 right-2">
          <div className={clsx(
            'w-2 h-2 rounded-full',
            activeTrack.readyState === 'live' && activeTrack.enabled 
              ? 'bg-green-500' 
              : 'bg-red-500'
          )} />
        </div>
      )}

      {/* Debug info - remove in production */}
      {process.env.NODE_ENV === 'development' && activeTrack && (
        <div className="absolute top-2 left-2 bg-black/70 text-white text-xs p-2 rounded font-mono">
          <div>Kind: {activeTrack.kind}</div>
          <div>State: {activeTrack.readyState}</div>
          <div>Enabled: {activeTrack.enabled ? 'Yes' : 'No'}</div>
          <div>ID: {activeTrack.id.slice(0, 8)}...</div>
        </div>
      )}
    </div>
  );
};